import { DeploymentRing } from '../protocol/gen/warinspace/v2/common_pb.js';
import type { FeatureSet } from '../protocol/gen/warinspace/v2/server_to_client_pb.js';

export const CUSTOM_LOBBIES = 'customLobbies';

export const DEBUG_TOOLS = 'debugTools';

export const SHIP_CUSTOMIZATION = 'shipCustomization';

export const OVERRIDES_STORAGE_KEY = 'wis.featureOverrides';

export type Ring = 'dev' | 'staging' | 'prod';

export interface FlagView {
  readonly key: string;
  readonly enabled: boolean;
  readonly overridden: boolean;
}

export type Overrides = Readonly<Record<string, boolean>>;

/** UNSPECIFIED is a server too old to say, and reads as prod — the closed door is the safe end of this. */
export function ringFromWire(ring: DeploymentRing): Ring {
  switch (ring) {
    case DeploymentRing.DEV:
      return 'dev';
    case DeploymentRing.STAGING:
      return 'staging';
    default:
      return 'prod';
  }
}

export function flagsFrom(set: FeatureSet): FlagView[] {
  return set.allFlags.map((flag) => ({
    key: flag.key,
    enabled: flag.enabled,
    overridden: flag.overridden,
  }));
}

export function readOverrides(store: Pick<Storage, 'getItem'> | null): Overrides {
  if (store === null) return {};
  let raw: string | null;
  try {
    raw = store.getItem(OVERRIDES_STORAGE_KEY);
  } catch {
    return {};
  }
  if (raw === null) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

  const out: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value === 'boolean') out[key] = value;
  }
  return out;
}

export function writeOverrides(store: Pick<Storage, 'setItem'> | null, next: Overrides): void {
  if (store === null) return;
  try {
    store.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(next));
  } catch {
    return;
  }
}

export function withOverride(current: Overrides, key: string, enabled: boolean | null): Overrides {
  const next = Object.fromEntries(Object.entries(current).filter(([name]) => name !== key));
  if (enabled !== null) next[key] = enabled;
  return next;
}
